import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wine, Coffee, Beer } from 'lucide-react';
import { supabase } from '@/lib/supabase';

const getraenkeDataFallback = {
  aperitivi: {
    title: 'Aperitivi',
    subtitle: 'Aperitifs',
    items: [
      { name: 'Glas Prosecco', size: '0,1 l', price: '4,50' },
      { name: 'Martini Bianco', size: '5 cl', price: '5,10' },
      { name: 'Campari mit Orangensaft oder Soda', size: '4 cl', price: '5,90' },
      { name: 'Aperol Spritz', size: '0,2 l', price: '7,50' },
      { name: 'Hugo', size: '0,2 l', price: '7,50' },
      { name: 'Crodino Spritz (Alkoholfrei)', size: '0,2 l', price: '5,50' }
    ]
  },
  longdrinks: {
    title: 'Longdrinks',
    subtitle: 'Cocktails',
    items: [
      { name: 'Whiskey Cola', size: '0,2 l', price: '9,50' },
      { name: 'Gin Tonic', size: '0,2 l', price: '9,50' },
      { name: 'Gin', size: '0,4 l', price: '8,50' }
    ]
  },
  alkoholfrei: {
    title: 'Alkoholfrei',
    subtitle: 'Erfrischungsgetränke',
    items: [
      { name: 'Acqua Gassata & Acqua Naturale', size: '0,50 l', price: '5,50' },
      { name: 'Mineralwasser mit oder ohne Kohlensäure', size: '0,75 l', price: '6,50' },
      { name: 'Saft', size: '0,2 l / 0,4 l', price: '3,50 / 4,50' },
      { name: 'Holunderschorle', size: '0,4 l', price: '4,50' },
      { name: 'Saftschorle', size: '0,2 l / 0,4 l', price: '2,90 / 3,90' },
      { name: 'Cola / Cola Zero / Fanta / Sprite', size: '0,2 l / 0,4 l', price: '3,10 / 4,10' },
      { name: 'Spezi', size: '0,5 l', price: '4,10' },
      { name: 'Schweppes Tonic Water', size: '0,2 l', price: '3,10' }
    ]
  },
  bier: {
    title: 'Birre',
    subtitle: 'Bier',
    items: [
      { name: 'Helles', size: '0,5 l', price: '4,50' },
      { name: 'Dunkel Bier', size: '0,5 l', price: '4,50' },
      { name: 'Radler', size: '0,5 l', price: '4,50' },
      { name: 'Alkoholfreies Bier', size: '0,5 l', price: '4,50' },
      { name: 'Weißbier', size: '0,5 l', price: '5,10' },
      { name: 'Weißbier Leicht', size: '0,5 l', price: '5,10' },
      { name: 'Weißbier Dunkel', size: '0,5 l', price: '5,10' },
      { name: 'Alkoholfrei Weißbier', size: '0,5 l', price: '5,10' },
      { name: 'Pils', size: '0,33 l', price: '4,10' },
      { name: 'Ruß', size: '0,5 l', price: '5,10' },
      { name: 'Cola-Weißbier', size: '0,5 l', price: '5,10' }
    ]
  },
  rotwein: {
    title: 'Vini Rossi',
    subtitle: 'Rotwein',
    items: [
      { name: 'Montepulciano', size: '0,25 l', price: '7,50' },
      { name: 'Nero D\'Avola', size: '0,25 l', price: '8,50' },
      { name: 'Weinschorle Rot', size: '0,25 l', price: '8,50' }
    ]
  },
  weisswein: {
    title: 'Vini Bianchi',
    subtitle: 'Weißwein',
    items: [
      { name: 'Trebbiano', size: '0,25 l', price: '7,50' },
      { name: 'Chardonnay', size: '0,25 l', price: '8,50' },
      { name: 'Lugana', size: '0,25 l', price: '9,50' },
      { name: 'Weinschorle Weiß', size: '0,25 l', price: '8,50' }
    ]
  },
  digestif: {
    title: 'Digestif',
    subtitle: 'Digestifs',
    items: [
      { name: 'Sambuca', size: '2 cl', price: '4,50' },
      { name: 'Bailey\'s Irish Cream', size: '2 cl', price: '4,50' },
      { name: 'Limoncello', size: '2 cl', price: '4,50' },
      { name: 'Grappa della Casa', size: '2 cl', price: '4,50' },
      { name: 'Williams Birne', size: '2 cl', price: '4,50' },
      { name: 'Fernet Branca', size: '2 cl', price: '4,50' },
      { name: 'Averna', size: '2 cl', price: '4,50' },
      { name: 'Ramazzotti', size: '2 cl', price: '4,50' },
      { name: 'Amaro del Capo', size: '2 cl', price: '4,50' },
      { name: 'Jägermeister', size: '2 cl', price: '4,50' }
    ]
  },
  warme: {
    title: 'Bevande Calde',
    subtitle: 'Warme Getränke',
    items: [
      { name: 'Espresso', size: '', price: '2,50' },
      { name: 'Espresso Corretto', size: '', price: '3,50' },
      { name: 'Doppio Espresso', size: '', price: '4,10' },
      { name: 'Tasse Kaffee', size: '', price: '3,10' },
      { name: 'Latte Macchiato', size: '', price: '3,90' },
      { name: 'Cappuccino', size: '', price: '3,50' },
      { name: 'Glas Tee', size: '', price: '2,90' }
    ]
  }
};

const categories = ['aperitivi', 'longdrinks', 'alkoholfrei', 'bier', 'rotwein', 'weisswein', 'digestif', 'warme'];

function buildMenuFromRows(rows) {
  const result = {};
  rows.forEach((row) => {
    if (!result[row.category]) {
      result[row.category] = { title: row.category_title, subtitle: row.category_subtitle || '', items: [] };
    }
    result[row.category].items.push({ name: row.name, size: row.size || '', price: row.price });
  });
  return result;
}

const weinkarte = {
  weissweine: [
    { name: 'Lugana ORA DOC 2017', region: 'Perla Del Garga-Lonato Lombardei', description: 'Leichte Anspielung an Akazien, Pfirsich, Apfel. Am Gaumen weich und ausgewogen.' },
    { name: 'Collefrisio-Pecorino IGT 2017', region: 'Az. Collefrisio-Abruzzen', description: 'Frische Aromen von Kräutern, Gräsern und Orangenzesten.' },
    { name: 'Baglio Oro-Guarì Inzolia', region: 'Baglio Oro-Sizilien', description: 'Nase nach weißem Pfirsich und sanfter Vanille, vollmundig und fruchtig.' },
    { name: 'Grechetto-Signae', region: 'Signae-Umbrien', description: 'Eindrucksvolle Frucht nach weißen Blüten mit zartem Säurespiel.' }
  ],
  rotweine: [
    { name: 'Gran Trio IGT 2016', region: 'San Marzano-Apulien', description: 'Primitivo, Malvasia, NegroAmaro. Erinnert an reife Pflaumen & Kirschmarmelade.' },
    { name: 'Tigliano IGT', region: 'Fattoria Poggio Nicchiaia-Toskana', description: 'Sangiovese, Cabernet, Syrah. Reife rote Beeren und Cassis.' },
    { name: 'Zinapfel-Old Wine', region: 'Masca de Tacco-Apulien', description: 'Komplexe Aromen von schwarzen Beeren und Pflaumen.' },
    { name: 'Donsar-Syrah', region: 'Baglio-Oro Sizilien', description: 'Duft von Beerenfrüchten, Pflaumen und Kirschen.' }
  ],
  rose: [
    { name: 'Masserei-Rosato Doc Negromaro', region: 'Schola Sarmenti-Apulien', description: 'Fruchtige Aromen roter Beeren und Kirschen.' }
  ]
};

export default function Getraenke() {
  const [activeCategory, setActiveCategory] = useState('aperitivi');
  const [showWeinkarte, setShowWeinkarte] = useState(false);
  const [getraenkeData, setGetraenkeData] = useState(getraenkeDataFallback);
  const [dynamicCategories, setDynamicCategories] = useState(categories);

  useEffect(() => {
    supabase
      .from('menu_items')
      .select('*')
      .eq('karte', 'getraenke')
      .order('sort_order')
      .then(({ data }) => {
        if (data && data.length > 0) {
          setGetraenkeData(buildMenuFromRows(data));
          const cats = [...new Set(data.map((r) => r.category))];
          setDynamicCategories(cats);
          if (!cats.includes(activeCategory)) setActiveCategory(cats[0]);
        }
      });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="getraenke-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/6hw2i7d5_Restaurant-Hallbergmoos-Italiener-65.jpg')`
          }}
        />
        <div className="absolute inset-0 bg-olive-900/70" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4"
        >
          <p className="font-accent text-gold text-2xl mb-4">Le Nostre Bevande</p>
          <h1 className="font-heading text-5xl md:text-6xl text-cream">Getränkekarte</h1>
          <p className="font-body text-cream/80 mt-4 text-lg">
            Von erlesenen Weinen bis zu köstlichen Kaffeekreationen
          </p>
        </motion.div>
      </section>

      {/* Toggle Buttons */}
      <section className="py-8 bg-cream">
        <div className="max-w-5xl mx-auto px-4 flex justify-center gap-4">
          <button
            data-testid="toggle-getraenke"
            onClick={() => setShowWeinkarte(false)}
            className={`px-6 py-3 font-body uppercase text-sm tracking-widest transition-all duration-300 ${
              !showWeinkarte
                ? 'bg-olive-900 text-cream'
                : 'bg-white text-olive-900 border border-olive-900/20 hover:bg-olive-100'
            }`}
          >
            Getränkekarte
          </button>
          <button
            data-testid="toggle-weinkarte"
            onClick={() => setShowWeinkarte(true)}
            className={`px-6 py-3 font-body uppercase text-sm tracking-widest transition-all duration-300 flex items-center gap-2 ${
              showWeinkarte
                ? 'bg-terracotta text-white'
                : 'bg-white text-olive-900 border border-olive-900/20 hover:bg-olive-100'
            }`}
          >
            <Wine size={18} />
            Weinkarte
          </button>
        </div>
      </section>

      {!showWeinkarte ? (
        /* Getränkekarte */
        <section className="py-16 md:py-24 bg-cream-dark">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {dynamicCategories.map((category) => (
                <button
                  key={category}
                  data-testid={`getraenke-tab-${category}`}
                  onClick={() => setActiveCategory(category)}
                  className={`px-3 py-2 md:px-5 md:py-3 font-body uppercase text-xs tracking-wider transition-all duration-300 ${
                    activeCategory === category
                      ? 'bg-olive-900 text-cream'
                      : 'bg-white text-olive-900 hover:bg-olive-100 border border-olive-900/20'
                  }`}
                >
                  {getraenkeData[category].title}
                </button>
              ))}
            </div>

            {/* Menu Items */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white p-8 md:p-12 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.1)]"
              >
                <div className="text-center mb-10">
                  <h2 className="font-heading text-3xl md:text-4xl text-olive-900 mb-2">
                    {getraenkeData[activeCategory].title}
                  </h2>
                  <p className="font-body text-muted-foreground italic">
                    {getraenkeData[activeCategory].subtitle}
                  </p>
                </div>

                <div className="space-y-4">
                  {getraenkeData[activeCategory].items.map((item, index) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      data-testid={`getraenke-item-${index}`}
                      className="flex justify-between items-center gap-4 pb-3 border-b border-cream-dark last:border-b-0"
                    >
                      <div className="flex-1">
                        <h3 className="font-heading text-lg text-olive-900">
                          {item.name}
                        </h3>
                      </div>
                      {item.size && (
                        <div className="font-body text-muted-foreground text-sm">
                          {item.size}
                        </div>
                      )}
                      <div className="font-heading text-lg text-terracotta whitespace-nowrap">
                        € {String(item.price).replace(/,(\d)(?=\s|$|\/)/g, ',$10')}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </section>
      ) : (
        /* Weinkarte */
        <section className="py-16 md:py-24 bg-cream-dark">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="font-accent text-terracotta text-xl mb-2">Selezione dei Vini</p>
              <h2 className="font-heading text-4xl text-olive-900">
                Unsere Weinempfehlungen
              </h2>
            </div>

            {/* Weißweine */}
            <div className="mb-12">
              <h3 className="font-heading text-2xl text-olive-900 mb-6 text-center">Weißweine</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {weinkarte.weissweine.map((wine, index) => (
                  <motion.div
                    key={wine.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white p-6 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.1)]"
                  >
                    <h4 className="font-heading text-xl text-olive-900 mb-1">{wine.name}</h4>
                    <p className="font-body text-terracotta text-sm mb-2">{wine.region}</p>
                    <p className="font-body text-muted-foreground text-sm">{wine.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Rotweine */}
            <div className="mb-12">
              <h3 className="font-heading text-2xl text-olive-900 mb-6 text-center">Rotweine</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {weinkarte.rotweine.map((wine, index) => (
                  <motion.div
                    key={wine.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white p-6 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.1)]"
                  >
                    <h4 className="font-heading text-xl text-olive-900 mb-1">{wine.name}</h4>
                    <p className="font-body text-terracotta text-sm mb-2">{wine.region}</p>
                    <p className="font-body text-muted-foreground text-sm">{wine.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Rosé */}
            <div>
              <h3 className="font-heading text-2xl text-olive-900 mb-6 text-center">Rosé Weine</h3>
              <div className="max-w-md mx-auto">
                {weinkarte.rose.map((wine, index) => (
                  <motion.div
                    key={wine.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-white p-6 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.1)]"
                  >
                    <h4 className="font-heading text-xl text-olive-900 mb-1">{wine.name}</h4>
                    <p className="font-body text-terracotta text-sm mb-2">{wine.region}</p>
                    <p className="font-body text-muted-foreground text-sm">{wine.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Info Section */}
      <section className="py-12 bg-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-body text-muted-foreground text-sm leading-relaxed">
            Alle Preise in Euro inkl. MwSt. • Zusatzstoffe und Allergene auf Anfrage
          </p>
        </div>
      </section>
    </div>
  );
}
